import { useTenantContext } from "@appretio/shared";
import { Button, Dialog, Label } from "@appretio/ui-kit";
import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from "react";
import { FeedbackScreenshotPicker } from "./components/feedbackScreenshotPicker/FeedbackScreenshotPicker";
import { FeedbackTransparencyPane } from "./components/feedbackTransparencyPane/FeedbackTransparencyPane";
import { FeedbackCategory, useFeedback } from "./FeedbackContext";
import * as styles from "./FeedbackModal.css";
import { useFeedbackPayload } from "./hooks/useFeedbackPayload";
import { useFeedbackScreenshots } from "./hooks/useFeedbackScreenshots";
import { useSubmitFeedback } from "./hooks/useSubmitFeedback";

/** Message budget — mirrors the API's MAX_MESSAGE_CHARS. */
const MAX_MESSAGE_CHARS = 4000;

/** Show the character counter once this few characters remain. */
const COUNTER_THRESHOLD_CHARS = 200;

/**
 * The label for each report kind, keyed by the generated API category union
 * — the ONE source of truth for both the offered options and their order. A
 * category added server-side fails this `Record` until it gets a label,
 * instead of silently disappearing from the modal.
 */
const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  data_wrong: "Data looks wrong",
  bug: "Something broke",
  idea: "Idea",
  other: "Other"
};

/**
 * The feedback modal singleton. Renders nothing until `useFeedback().open()`
 * is called (header button, "Report this value" grid item, …). Mount once
 * inside <FeedbackProvider>, next to the app shell.
 *
 * Its own responsibility is the FORM: category, message, and the send gate.
 * The capture pipeline (`useFeedbackPayload`), the attachments
 * (`useFeedbackScreenshots` + `FeedbackScreenshotPicker`) and the GDPR
 * disclosure (`FeedbackTransparencyPane`) each own their concern.
 */
export function FeedbackModal() {
  const { isOpen, prefill, close } = useFeedback();
  const { currentOrg } = useTenantContext();
  const submitFeedback = useSubmitFeedback();
  const { preview, capture, recapture } = useFeedbackPayload();
  const {
    screenshots,
    error: screenshotError,
    addImages,
    removeScreenshot,
    reset: resetScreenshots
  } = useFeedbackScreenshots();

  // While the POST is in flight the whole form locks: the Dialog already
  // blocks Escape / backdrop / Cancel / X via `isLoading`, and this flag
  // disables every input so nothing can change (or be dismissed) mid-flight.
  const isSubmitting = submitFeedback.isPending;

  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState("");
  // Synchronous reentry guard: two rapid Send clicks land in the same tick,
  // before the `isPending` rerender can disable the button — React state is
  // a render too late, only a ref blocks the second call.
  const submitInFlightRef = useRef(false);

  // Single source of truth for "can this report be sent": the Send button's
  // disabled prop, the confirm guard, and the hint below all derive from
  // these — the predicate must never be restated inline.
  const missingCategory = !category;
  const missingMessage = !message.trim();
  const sendBlocked = missingCategory || missingMessage;
  // Name exactly what is still missing — a silently disabled button reads
  // as broken. Null once both are present.
  const sendBlockedHint =
    missingCategory && missingMessage
      ? "Choose a category and write a message to send."
      : missingCategory
        ? "Choose a category to send."
        : missingMessage
          ? "Write a message to send."
          : null;

  // Set after a successful submit: the shareable restore-view URL for the
  // minted report. Non-null flips the modal into its brief success state
  // (link + copy button + Done) instead of closing outright.
  const [restoreLink, setRestoreLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // The clipboard write failed, or the API is absent (insecure context).
  // The link itself is still on screen and selectable — say so rather than
  // claim a copy that never happened.
  const [copyFailed, setCopyFailed] = useState(false);

  // Reset the form each time the modal re-opens, seeding from the prefill.
  // The transparency pane needs no reset here: the Dialog unmounts its
  // content on close, so the pane's expanded/Copied state dies with it.
  useEffect(() => {
    if (!isOpen) return;
    setCategory(prefill?.category ?? null);
    setMessage("");
    resetScreenshots();
    setRestoreLink(null);
    setLinkCopied(false);
    setCopyFailed(false);
  }, [isOpen, prefill, resetScreenshots]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Cmd/Ctrl+A inside the modal must select the focused element's own
    // text — and nothing more. The dialog portals into <body>, so the
    // keydown would otherwise bubble on to body/document where page- or
    // grid-level select-all listeners can react to it. Stop propagation at
    // the modal boundary. Escape is untouched: the Dialog's dismiss/lock
    // semantics live in Radix's own document-level handler.
    //
    // No preventDefault here — the native select-the-field behavior
    // (textarea, inputs) is exactly what we want to keep. The transparency
    // pane, which has no native behavior worth keeping, replaces the default
    // itself in its own handler further down the bubble path.
    if (!((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a"))
      return;
    event.stopPropagation();
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    if (isSubmitting) return; // form is locked mid-flight
    const images = Array.from(event.clipboardData?.files ?? []).filter((file) =>
      file.type.startsWith("image/")
    );
    if (images.length > 0) void addImages(images);
  }

  async function handleConfirm() {
    if (restoreLink) {
      // Success state: the confirm button is "Done" — just dismiss.
      close();
      return true;
    }
    if (submitInFlightRef.current) return false; // rapid double-click
    // sendBlocked is the shared gate; the redundant `!category` only narrows
    // the type for the payload below (TS can't see through sendBlocked).
    if (sendBlocked || !category) return false;
    submitInFlightRef.current = true;
    try {
      const feedbackId = await submitFeedback.mutateAsync({
        category,
        message: message.trim(),
        // Captured NOW — page getters, diagnostics, URL and client clock all
        // reflect the moment the user hit Send, not when the modal opened.
        ...capture(),
        screenshots:
          screenshots.length > 0
            ? screenshots.map((screenshot) => screenshot.dataUri)
            : null
      });
      if (feedbackId) {
        // Brief success state: surface the restore link (the reviewer-side
        // "open the reporter's view" URL) instead of closing outright.
        // Tenant rides as ?org= from the same source the payload uses.
        const orgQuery = currentOrg
          ? `?org=${encodeURIComponent(currentOrg.name)}`
          : "";
        setRestoreLink(
          `${window.location.origin}/restore-feedback/${feedbackId}${orgQuery}`
        );
        return false;
      }
      close();
      return true;
    } catch {
      // The global mutation cache already raised the error toast; keep the
      // modal (and everything the user wrote) so nothing is lost.
      return false;
    } finally {
      submitInFlightRef.current = false;
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Mid-flight dismissal would silently drop the pending report's
        // outcome — the Dialog blocks the triggers, this guards the rest.
        if (!open && !isSubmitting) close();
      }}
      title="Send feedback"
      description="Tell us what looks wrong, broke, or could be better."
      // This modal is itself a Dialog: without this it grows a trigger that
      // reports on itself, and registers itself as the topmost open dialog.
      hideFeedbackIcon
      confirmText={restoreLink ? "Done" : "Send"}
      cancelText="Cancel"
      onConfirm={handleConfirm}
      isLoading={submitFeedback.isPending}
      confirmDisabled={!restoreLink && sendBlocked}
    >
      {restoreLink ? (
        <div className={styles.successBlock}>
          <p className={styles.successTitle}>Feedback sent.</p>
          <p className={styles.successHint}>
            Reviewers can reopen your exact view from this link:
          </p>
          <code className={styles.restoreLinkUrl}>{restoreLink}</code>
          <div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                // Same contract as the transparency pane's Copy: "Copied"
                // only once the async write RESOLVED. A rejected write (or a
                // clipboard API absent in an insecure context) copied
                // nothing, so say so — the visible URL above stays
                // selectable either way.
                const write = navigator.clipboard?.writeText(restoreLink);
                if (!write) {
                  setCopyFailed(true);
                  return;
                }
                write
                  .then(() => {
                    setLinkCopied(true);
                    setCopyFailed(false);
                  })
                  .catch(() => {
                    setLinkCopied(false);
                    setCopyFailed(true);
                  });
              }}
            >
              {linkCopied ? "Copied" : "Copy restore link"}
            </Button>
          </div>
          {copyFailed && (
            // aria-live so a screen reader hears the failed copy; the same
            // muted-error idiom the screenshot picker uses for its errors.
            <span className={styles.copyFailedText} aria-live="polite">
              Copy failed — select the link above and copy it manually.
            </span>
          )}
        </div>
      ) : (
        <div
          className={styles.formContainer}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
        >
          <div
            role="group"
            aria-label="Category"
            className={styles.categoryRow}
          >
            {(
              Object.entries(CATEGORY_LABELS) as [FeedbackCategory, string][]
            ).map(([value, label]) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={category === value ? "primary" : "secondary"}
                aria-pressed={category === value}
                disabled={isSubmitting}
                onClick={() => setCategory(value)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div>
            <Label label="What happened?" htmlFor="feedback-message" />
            <textarea
              id="feedback-message"
              className={styles.messageArea}
              placeholder="The more specific, the better — which product, store, or number? What did you expect to see?"
              value={message}
              maxLength={MAX_MESSAGE_CHARS}
              disabled={isSubmitting}
              onChange={(event) => setMessage(event.target.value)}
            />
            {message.length >= MAX_MESSAGE_CHARS - COUNTER_THRESHOLD_CHARS && (
              <div className={styles.charCounter}>
                {message.length} / {MAX_MESSAGE_CHARS}
              </div>
            )}
          </div>

          <FeedbackScreenshotPicker
            screenshots={screenshots}
            error={screenshotError}
            isDisabled={isSubmitting}
            onAddFiles={(files) => void addImages(files)}
            onRemove={removeScreenshot}
          />

          <FeedbackTransparencyPane
            payload={preview}
            screenshotCount={screenshots.length}
            isDisabled={isSubmitting}
            onExpand={recapture}
          />

          <p className={styles.consentNote}>
            We collect the technical details shown under &ldquo;What will be
            sent&rdquo; — page, selections, recent errors, your user identity
            and network address — for debugging and product analysis. By
            pressing Send you consent to this.
          </p>

          {sendBlockedHint && (
            // aria-live so screen readers hear the requirement change as the
            // user fills the form; visually it sits closest to the Send button.
            <p className={styles.sendBlockedHint} aria-live="polite">
              {sendBlockedHint}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
